import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ClaudeLogo from "@/components/ClaudeLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BG = "https://images.unsplash.com/photo-1671716784499-a3d26826d844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHx3YXJtJTIwYWJzdHJhY3QlMjBtaW5pbWFsJTIwYmFja2dyb3VuZCUyMHNhbmQlMjBjb3JhbHxlbnwwfHx8fDE3ODcwNDg0MjJ8MA&ixlib=rb-4.1.0&q=85";

export default function LoginScreen({ onSuccess }) {
  const [key, setKey] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim() || !email.trim()) {
      toast.error("Please enter both your access key and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/access/verify`, { key: key.trim(), email: email.trim() });
      onSuccess({ key: key.trim(), email: email.trim().toLowerCase() });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not verify your access. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex items-center justify-center px-4 py-12">
      <img
        src={BG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-[#F9F8F6]/40" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md"
      >
        <div
          data-testid="login-card"
          className="rounded-2xl border border-white/50 bg-white/80 backdrop-blur-xl p-8 sm:p-10 shadow-[0_8px_40px_rgba(20,20,19,0.08)]"
        >
          <div className="flex items-center gap-3 mb-8">
            <ClaudeLogo className="h-9 w-9" />
            <span className="font-serif text-2xl font-medium tracking-tight text-[#141413]">
              Claude Access
            </span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#141413] leading-tight">
            Retrieve your verification emails
          </h1>
          <p className="mt-3 text-base text-[#57534E] leading-relaxed">
            Enter your access key and email address to view your latest Claude login codes and messages.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="key" className="text-sm font-medium text-[#141413]">
                Access key
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A29E]" />
                <Input
                  id="key"
                  data-testid="key-input"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="CLD-XXXXXXXXXXXX"
                  autoComplete="off"
                  className="pl-10 h-12 font-mono text-sm bg-white border-[#E7E5E4] focus-visible:ring-[#CC785C]/40 focus-visible:border-[#CC785C]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#141413]">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A29E]" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="off"
                  className="pl-10 h-12 bg-white border-[#E7E5E4] focus-visible:ring-[#CC785C]/40 focus-visible:border-[#CC785C]"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full h-12 rounded-lg bg-[#CC785C] hover:bg-[#B8654D] text-white font-medium transition-colors duration-200 group"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying…
                </>
              ) : (
                <>
                  Access inbox
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-xs text-[#78716C]">
            <ShieldCheck className="h-3.5 w-3.5 text-[#CC785C]" />
            <span>Private access. Only authorized emails linked to your key are shown.</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
